// =============================================================================
// Invoice Reconciliation — adapted from POS frmInvoiceReconciliation. The legacy
// form reconciled NAB/Velocity gateway transactions against POS payments; we use
// Stripe, so this reconciles each non-void invoice's total against the sum of its
// recorded payments over a date range, flagging unpaid / underpaid / overpaid.
// =============================================================================
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, AlertTriangle } from 'lucide-react'
import { reconcileInvoices, type ReconRow } from '../services/data'
import { PageHeader, Card, Spinner, Button } from '../components/shared/ui'
import { currency, dateTime } from '../lib/format'
import { toast } from 'react-toastify'

const todayStr = () => new Date().toISOString().slice(0, 10)
const nextDay = (d: string) => { const x = new Date(d); x.setDate(x.getDate() + 1); return x.toISOString().slice(0, 10) }

const FLAG_STYLE: Record<ReconRow['flag'], string> = {
  unpaid: 'bg-red-100 text-red-700',
  underpaid: 'bg-amber-100 text-amber-700',
  overpaid: 'bg-purple-100 text-purple-700',
  balanced: 'bg-emerald-100 text-emerald-700',
}

export default function Reconciliation() {
  const navigate = useNavigate()
  const [start, setStart] = useState(todayStr())
  const [end, setEnd] = useState(todayStr())
  const [rows, setRows] = useState<ReconRow[]>([])
  const [loading, setLoading] = useState(false)
  const [onlyIssues, setOnlyIssues] = useState(true)

  const run = useCallback(async () => {
    setLoading(true)
    try { setRows(await reconcileInvoices(start, nextDay(end))) }
    catch (e) { console.error(e); toast.error('Reconciliation failed') }
    finally { setLoading(false) }
  }, [start, end])

  useEffect(() => { run() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const shown = onlyIssues ? rows.filter(r => r.flag !== 'balanced') : rows
  const counts = rows.reduce((a, r) => { a[r.flag] = (a[r.flag] ?? 0) + 1; return a }, {} as Record<string, number>)

  return (
    <div>
      <PageHeader title="Invoice Reconciliation" subtitle="Match invoice totals against recorded payments" />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm"><span className="block text-gray-600 mb-1">From</span>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2" /></label>
          <label className="text-sm"><span className="block text-gray-600 mb-1">To</span>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2" /></label>
          <Button onClick={run}><Search size={16} /> Reconcile</Button>
          <label className="text-sm flex items-center gap-2 ml-auto">
            <input type="checkbox" checked={onlyIssues} onChange={e => setOnlyIssues(e.target.checked)} className="h-4 w-4" />
            Show only discrepancies
          </label>
        </div>
        {rows.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <span className="px-2 py-0.5 rounded bg-red-100 text-red-700">Unpaid: {counts.unpaid ?? 0}</span>
            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700">Underpaid: {counts.underpaid ?? 0}</span>
            <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700">Overpaid: {counts.overpaid ?? 0}</span>
            <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Balanced: {counts.balanced ?? 0}</span>
          </div>
        )}
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-6"><Spinner label="Reconciling…" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-gray-600">
                  <th className="text-left px-3 py-2.5 font-semibold">Invoice #</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Date</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Payment</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Total</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Paid</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Difference</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Flag</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500">{rows.length === 0 ? 'Run a reconciliation for a date range.' : 'No discrepancies — everything balances.'}</td></tr>
                ) : shown.map(r => (
                  <tr key={r.id} onClick={() => navigate(`/transactions/${r.id}`)} className="border-b border-gray-100 cursor-pointer hover:bg-green-50">
                    <td className="px-3 py-2 font-medium text-gray-900">#{r.invoice_number ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{dateTime(r.invoice_date)}</td>
                    <td className="px-3 py-2 text-gray-600">{r.status || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{r.payment_method || '—'}</td>
                    <td className="px-3 py-2 text-right">{currency(r.total)}</td>
                    <td className="px-3 py-2 text-right">{currency(r.paid)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${r.diff === 0 ? 'text-gray-500' : r.diff < 0 ? 'text-red-600' : 'text-purple-600'}`}>{currency(r.diff)}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs capitalize inline-flex items-center gap-1 ${FLAG_STYLE[r.flag]}`}>
                        {r.flag !== 'balanced' && <AlertTriangle size={12} />}{r.flag}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
